/**
 * SMS length maths.
 *
 * A body that fits the GSM-7 alphabet packs 160 characters into one SMS. A
 * single character outside it — Czech/Slovak diacritics (ě š č ř ž ů), emoji —
 * forces the whole message to UCS-2, where one SMS holds only 70. Once a
 * message splits, every part gives up room to the concatenation header, so the
 * per-part budget drops to 153 and 67.
 *
 * Channel::SmsBrana rejects an over-long body with error 12, so the counter in
 * the UI has to do the same maths the gateway does instead of always showing
 * /160.
 */

const GSM7_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡' +
  'ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

// Reachable only through the GSM-7 escape sequence, so two septets each.
const GSM7_EXTENDED = '^{}\\[~]|€';

// Returns null as soon as a character has no GSM-7 representation. `every`
// short-circuits there, so the rest of the body is not scanned.
const gsm7Length = chars => {
  let septets = 0;
  const representable = chars.every(char => {
    if (GSM7_BASIC.includes(char)) {
      septets += 1;
      return true;
    }
    if (GSM7_EXTENDED.includes(char)) {
      septets += 2;
      return true;
    }
    return false;
  });

  return representable ? septets : null;
};

// UCS-2 is billed per 16-bit code unit, so an astral character costs two.
const ucs2Length = chars =>
  chars.reduce(
    (total, char) => total + (char.codePointAt(0) > 0xffff ? 2 : 1),
    0
  );

/**
 * @param {string} text
 * @returns {{encoding: string, length: number, limit: number, segments: number}}
 *   `limit` is the budget that applies at this length — the single-SMS one
 *   until the body outgrows it, the per-part one after.
 */
export const smsSegments = (text = '') => {
  const chars = [...text];
  const septets = gsm7Length(chars);
  const isGsm7 = septets !== null;
  const length = isGsm7 ? septets : ucs2Length(chars);
  const single = isGsm7 ? 160 : 70;
  const concatenated = isGsm7 ? 153 : 67;
  const limit = length <= single ? single : concatenated;

  return {
    encoding: isGsm7 ? 'GSM-7' : 'UCS-2',
    length,
    limit,
    segments: length === 0 ? 0 : Math.ceil(length / limit),
  };
};
